import * as R from "ramda";

import { array, arrayOf, bool, shape, string } from "prop-types";
import { insertAds, insertAdsAndManualPeople } from "./helpers.js";
import { useEffect, useRef, useState } from "react";

import AdItem from "./AdItem.jsx";
import Loader from "../Loader/Loader.jsx";
import PersonItem from "./PersonItem.jsx";
import cx from "classnames";
import { mockApiResponse } from "./mockData.js";
import useQueryString from "../../common/useQueryString.js";

const adShape = shape({
  href: string.isRequired,
  text: string.isRequired,
  target: string,
});

const propTypes = {
  storybook: bool,
  dataModel: shape({
    url: string.isRequired,
    loadMoreText: string.isRequired,
    filterQuerystringKey: string.isRequired,
    cta: shape({
      href: string.isRequired,
      text: string.isRequired,
      target: string,
    }),
    ad1: adShape,
    ad2: adShape,
    boards: array,
    leadership: array,
    filters: arrayOf(
      shape({ label: string.isRequired, value: string.isRequired }),
    ),
  }).isRequired,
  dataQuery: shape({
    pageId: string.isRequired,
    listingId: string.isRequired,
  }).isRequired,
};

const PeopleListing = (props) => {
  const {
    dataModel: {
      storybook,
      url,
      loadMoreText,
      filters,
      filterQuerystringKey,
      cta,
      ad1,
      ad2,
      boards,
      leadership,
    },
    dataQuery: { pageId, listingId },
  } = props;

  const [response, setResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useQueryString(filterQuerystringKey);
  const [adsInserted, setAdsInserted] = useState(false);
  const [numAds, setNumAds] = useState(0);
  const [extraPeople, setExtraPeople] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [kelloggBoards, setKelloggBoards] = useState(false);

  //ref to remember the previous selected filter
  const filterRef = useRef(activeFilter);

  const leadershipKey = "{C96E7A37-F04C-444A-B104-A120F5DD3F38}";
  const pagination = false;

  // handle inserting manual items to results
  useEffect(() => {
    if (kelloggBoards || adsInserted) {
      return;
    }

    if (response !== null && activeFilter !== leadershipKey) {
      setResponse(
        R.evolve(
          {
            results: (results) => insertAds(results, ad1, ad2, setNumAds),
          },
          response,
        ),
      );
      setExtraPeople(0);
      setAdsInserted(true);
    } else if (response !== null && activeFilter === leadershipKey) {
      //leadership tab
      setResponse(
        R.evolve(
          {
            results: (results) =>
              insertAdsAndManualPeople(
                results,
                leadership,
                setExtraPeople,
                ad1,
                ad2,
                setNumAds,
              ),
          },
          response,
        ),
      );
      setAdsInserted(true);
    }
  }, [
    response,
    adsInserted,
    ad1,
    ad2,
    kelloggBoards,
    activeFilter,
    leadership,
  ]);

  //network request for dynamic people items
  useEffect(() => {
    if (storybook === true) {
      setResponse((prevState) =>
        R.mergeDeepWithKey(
          (k, l, r) => (k === "results" ? R.concat(l, r) : r),
          prevState,
          mockApiResponse,
        ),
      );
    } else if (activeFilter !== null) {
      setIsLoading(true);
      fetch(
        `${url}?listingId=${listingId}&pageId=${pageId}&${filterQuerystringKey}=${activeFilter}&page=${currentPage}`,
      )
        .then((response) => response.json())
        .then((data) => {
          //check to see if the filter has changed (was load more clicked?)
          if (filterRef.current === activeFilter) {
            setResponse((prevState) =>
              prevState
                ? //take new data for all keys except results where we combine old + new
                  R.mergeDeepWithKey(
                    (k, l, r) => (k === "results" ? R.concat(l, r) : r),
                    prevState,
                    data,
                  )
                : data,
            );
          } else {
            //if the active filter has changed, set new response data
            setResponse(data);
            setCurrentPage(1);
            setNumAds(0);
            setAdsInserted(false);
            //update the filter ref
            filterRef.current = activeFilter;
          }
        })
        .finally(() => {
          setIsLoading(false);
        })
        .catch((e) => console.warn(e));
    }
  }, [
    url,
    listingId,
    pageId,
    currentPage,
    storybook,
    filterQuerystringKey,
    activeFilter,
  ]);

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <section className="people-listing">
      <div className="people-listing__filters">
        {filters.map((filter, i) => (
          <button
            className={cx("people-listing__filter-btn", {
              "people-listing__filter-btn--active":
                activeFilter === filter.value,
            })}
            type="button"
            onClick={() => {
              setKelloggBoards(false);
              setActiveFilter(filter.value);
            }}
            key={i}
          >
            {filter.label}
          </button>
        ))}
        {boards?.length > 0 && (
          <button
            className={cx("people-listing__filter-btn", {
              "people-listing__filter-btn--active": kelloggBoards === true,
            })}
            type="button"
            onClick={() => {
              setActiveFilter(null);
              setResponse(null);
              setAdsInserted(false);
              setKelloggBoards(true);
            }}
          >
            Kellogg Boards
          </button>
        )}
        {cta && (
          <a
            className="people-listing__cta"
            href={cta.href}
            target={cta.target ? cta.target : "_self"}
            rel="noreferrer"
          >
            {cta.text}
          </a>
        )}
      </div>

      {isLoading && (
        <div className="people-listing__loader">
          <Loader />
        </div>
      )}

      {!isLoading && (
        <div className="people-listing__persons-wrapper">
          {!kelloggBoards &&
            response?.results?.map((item, i) => {
              switch (item.type) {
                case "person":
                  return <PersonItem key={i} {...item} />;
                case "ad":
                  return <AdItem key={i} {...item} />;
                default:
                  return <div>error</div>;
              }
            })}
          {kelloggBoards &&
            boards.map((item, i) => {
              switch (item.type) {
                case "person":
                  return <PersonItem key={i} {...item} />;
                case "ad":
                  return <AdItem key={i} {...item} />;
                default:
              }
            })}
        </div>
      )}

      {isLoading && <Loader />}

      {!kelloggBoards &&
        response?.totalResults >
          //subtract all of the manual items to find if there are more to load
          response?.results.length - numAds - extraPeople && (
          <div className="people-listing__load-more">
            <button
              className="call-to-action call-to-action--outline"
              onClick={() => handlePageChange(currentPage + 1)}
              type="button"
            >
              {loadMoreText}
            </button>
          </div>
        )}
    </section>
  );
};

PeopleListing.propTypes = propTypes;
export default PeopleListing;
